"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CaretRight } from "@phosphor-icons/react";
import { Fragment, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { useResizeObserver } from "usehooks-ts";
import { breadcrumb, } from "@filadd/phi-plus-styled-system/recipes";
import { Button } from "../Button";
import { Menu } from "../Menu";
import { Crumb } from "./Crumb";
import { useBreadcrumbs } from "./useBreadcrumbs";
// we need to recompute the crumbs twice to ensure that the overflow menu
// is taken into account when calculating the visible crumbs
const RECOMPUTE_ROUNDS = 2;
export const Breadcrumbs = (_a) => {
    var { crumb, size } = _a, props = __rest(_a, ["crumb", "size"]);
    const baseBreadcrumbs = useBreadcrumbs();
    const breadcrumbs = useMemo(() => (crumb != null ? [...baseBreadcrumbs, crumb] : baseBreadcrumbs), [baseBreadcrumbs, crumb]);
    const [visibleCrumbs, setVisibleCrumbs] = useState(breadcrumbs);
    const [overflowCrumbs, setOverflowCrumbs] = useState([]);
    const [recomputeRounds, setRecomputeRounds] = useState(RECOMPUTE_ROUNDS);
    const containerRef = useRef(null);
    useLayoutEffect(() => {
        var _a, _b;
        if (containerRef.current == null || recomputeRounds === 0) {
            return;
        }
        // determine which crumbs are visible and which are in the overflow menu
        // based on the available width of the container and the width of the crumbs
        const availableWidth = containerRef.current.offsetWidth;
        const computedStyle = window.getComputedStyle(containerRef.current);
        const gap = parseFloat(computedStyle.gap) || 0;
        let overflowMenuWidth = 0;
        const crumbWidths = [];
        for (let i = 1; i < containerRef.current.childNodes.length - 1; i += 2) {
            const crumbElement = containerRef.current.childNodes[i - 1];
            const caretElement = containerRef.current.childNodes[i];
            // use the width of both the crumbs and the caret; add the gap between them
            // and the gap between the caret and the next crumb
            const width = crumbElement.offsetWidth + caretElement.offsetWidth + gap * 2;
            if (crumbElement.querySelector("button") !== null) {
                overflowMenuWidth = width;
            }
            else {
                crumbWidths.push(width);
            }
        }
        const lastCrumbWidth = (_b = (_a = containerRef.current.childNodes[containerRef.current.childNodes.length - 1]) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
        let usedWidth = overflowMenuWidth + lastCrumbWidth;
        let visibleCount = 1;
        for (let i = crumbWidths.length - 1; i >= 0; i -= 1) {
            if (usedWidth + crumbWidths[i] <= availableWidth) {
                usedWidth += crumbWidths[i];
                visibleCount += 1;
            }
            else {
                break;
            }
        }
        const newVisibleCrumbs = breadcrumbs.slice(-visibleCount);
        const newOverflowCrumbs = breadcrumbs.slice(0, -visibleCount);
        setVisibleCrumbs(newVisibleCrumbs);
        setOverflowCrumbs(newOverflowCrumbs);
        setRecomputeRounds((prev) => prev - 1);
    }, [breadcrumbs, recomputeRounds]);
    useResizeObserver({
        ref: containerRef,
        onResize: () => {
            // when the containers resize, reset the visible and overflow crumbs
            // and let the layout effect recalculate the visible crumbs
            setVisibleCrumbs(breadcrumbs);
            setOverflowCrumbs([]);
            setRecomputeRounds(RECOMPUTE_ROUNDS);
        },
    });
    const classes = breadcrumb({
        size,
        overflow: recomputeRounds === 0 && overflowCrumbs.length > 0
            ? "overflow"
            : "normal",
    });
    return (_jsx("nav", Object.assign({}, props, { children: _jsxs("ul", { className: classes.root, ref: containerRef, children: [overflowCrumbs.length > 0 ? (_jsxs(_Fragment, { children: [_jsx("li", { className: classes.crumb, children: _jsxs(Menu.Root, { children: [_jsx(Menu.Trigger, { asChild: true, children: _jsx(Button, { size: "md", variant: "ghost", color: "neutral", className: classes.crumb, responsive: true, children: "..." }) }), _jsx(Menu.Positioner, { children: _jsx(Menu.Content, { children: overflowCrumbs.map((currentCrumb) => (_jsx(Menu.Item, { value: currentCrumb.label, children: _jsx(Crumb, { as: "div", crumb: currentCrumb, className: classes.crumb }) }, currentCrumb.label))) }) })] }) }), _jsx("li", { className: classes.crumb, children: _jsx(CaretRight, {}) })] })) : null, visibleCrumbs.map((currentCrumb, index) => (_jsxs(Fragment, { children: [_jsx(Crumb, { as: "li", crumb: currentCrumb, className: classes.crumb, disabled: index === visibleCrumbs.length - 1 }), index < visibleCrumbs.length - 1 ? (_jsx("li", { className: classes.crumb, children: _jsx(CaretRight, {}) })) : null] }, currentCrumb.label)))] }) })));
};
//# sourceMappingURL=Breadcrumbs.js.map